import { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const isSuccess = (data) =>
  !data?.responseCode || String(data.responseCode) === "00";

const extractRows = (data) => {
  const value = data?.response ?? data?.data ?? data?.result ?? [];
  if (Array.isArray(value)) return value;
  return value && typeof value === "object" ? [value] : [];
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.responseMessage ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

export default function useBulkOptionGroups() {
  const { api, userID } = useContext(GlobalContext);

  const bulkCatalogIdRef = useRef("");
  const bulkCatalogPromiseRef = useRef(null);

  const resolveBulkCatalogId = async ({ force = false } = {}) => {
    const cached = String(bulkCatalogIdRef.current || "").trim();
    if (!force && cached) return cached;
    if (!force && bulkCatalogPromiseRef.current)
      return bulkCatalogPromiseRef.current;

    const request = (async () => {
      try {
        const usCode = String(userID || "").trim();
        if (!usCode) return "";

        const res = await api.post("/Inventory/getCatalogIdUSCode", { usCode });
        if (String(res?.data?.responseCode ?? "") !== "00") {
          toast.error(
            res?.data?.responseMessage ||
              "Failed to fetch catalogue mapping ID",
          );
          return "";
        }

        const response = res?.data?.response;
        const mappingId = Array.isArray(response)
          ? response?.[0]?.mapping_id
          : response?.mapping_id;
        const catalogId = String(mappingId ?? "").trim();
        if (!catalogId) {
          toast.error("Catalogue mapping ID not found");
          return "";
        }

        bulkCatalogIdRef.current = catalogId;
        return catalogId;
      } catch (error) {
        console.error(
          "getCatalogIdUSCode error:",
          error?.response?.data || error,
        );
        toast.error(
          error?.response?.data?.responseMessage ||
            error?.response?.data?.message ||
            error?.message ||
            "Error fetching catalogue mapping ID",
        );
        return "";
      } finally {
        bulkCatalogPromiseRef.current = null;
      }
    })();

    bulkCatalogPromiseRef.current = request;
    return request;
  };

  const [optionGroupsLoading, setOptionGroupsLoading] = useState(false);
  const [optionGroupSaving, setOptionGroupSaving] = useState(false);
  const [optionGroupDeletingId, setOptionGroupDeletingId] = useState("");
  const [optionGroupItemsLoading, setOptionGroupItemsLoading] = useState(false);
  const [optionGroupItemSaving, setOptionGroupItemSaving] = useState(false);
  const [optionGroupItemDeletingId, setOptionGroupItemDeletingId] =
    useState("");

  const getOptionGroups = async (optionGroupId = "") => {
    try {
      setOptionGroupsLoading(true);

      const res = await api.post("/BulkCatalogue/optionGroupsGet", {
        catalog_id: await resolveBulkCatalogId(),
        option_group_id: String(optionGroupId ?? "").trim(),
      });

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch option groups",
        );
        return [];
      }

      return extractRows(res?.data);
    } catch (error) {
      console.error("optionGroupsGet error:", error?.response?.data || error);
      toast.error(getErrorMessage(error, "Error fetching option groups"));
      return [];
    } finally {
      setOptionGroupsLoading(false);
    }
  };

  const buildPayload = (form = {}, includeId = false) => ({
    ...(includeId
      ? { option_group_id: String(form?.option_group_id ?? "").trim() }
      : {}),
    restaurant_id: String(form?.restaurant_id ?? "").trim(),
    group_name: String(form?.group_name ?? "").trim(),
    description: String(form?.description ?? "").trim(),
    status: String(form?.status ?? "ACTIVE").trim(),
  });

  const insertOptionGroup = async (form = {}) => {
    if (optionGroupSaving) return false;

    const payload = buildPayload(form);

    if (!payload.restaurant_id) {
      toast.error("Restaurant is required");
      return false;
    }

    if (!payload.group_name) {
      toast.error("Group name is required");
      return false;
    }

    try {
      setOptionGroupSaving(true);

      const res = await api.post("/BulkCatalogue/optionGroupsInsert", payload);

      if (!isSuccess(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to add option group");
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Option group added successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "optionGroupsInsert error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error adding option group"));
      return false;
    } finally {
      setOptionGroupSaving(false);
    }
  };

  const updateOptionGroup = async (form = {}) => {
    if (optionGroupSaving) return false;

    const payload = buildPayload(form, true);

    if (!payload.option_group_id) {
      toast.error("Option Group ID is required");
      return false;
    }

    if (!payload.restaurant_id) {
      toast.error("Restaurant is required");
      return false;
    }

    if (!payload.group_name) {
      toast.error("Group name is required");
      return false;
    }

    try {
      setOptionGroupSaving(true);

      const res = await api.post("/BulkCatalogue/optionGroupsUpdate", payload);

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to update option group",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Option group updated successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "optionGroupsUpdate error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error updating option group"));
      return false;
    } finally {
      setOptionGroupSaving(false);
    }
  };

  const deleteOptionGroup = async (optionGroupId) => {
    const id = String(optionGroupId ?? "").trim();

    if (!id || optionGroupDeletingId) return false;

    try {
      setOptionGroupDeletingId(id);

      const res = await api.post("/BulkCatalogue/optionGroupsDelete", {
        option_group_id: id,
      });

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to delete option group",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Option group deleted successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "optionGroupsDelete error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error deleting option group"));
      return false;
    } finally {
      setOptionGroupDeletingId("");
    }
  };

  const getOptionGroupItems = async (optionGroupItemId = "") => {
    try {
      setOptionGroupItemsLoading(true);
      const res = await api.post("/BulkCatalogue/optionGroupItemsGet", {
        catalog_id: await resolveBulkCatalogId(),
        option_group_item_id: String(optionGroupItemId ?? "").trim(),
      });
      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch option group items",
        );
        return [];
      }
      return extractRows(res?.data);
    } catch (error) {
      console.error(
        "optionGroupItemsGet error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error fetching option group items"));
      return [];
    } finally {
      setOptionGroupItemsLoading(false);
    }
  };

  const buildOptionGroupItemPayload = (form = {}, includeId = false) => ({
    ...(includeId
      ? {
          option_group_item_id: String(form?.option_group_item_id ?? "").trim(),
        }
      : {}),
    option_group_id: String(form?.option_group_id ?? "").trim(),
    package_id: String(form?.package_id ?? "").trim(),
    item_id: String(form?.item_id ?? "").trim(),
    variant_id: String(form?.variant_id ?? "").trim(),
    quantity_per_person: String(form?.quantity_per_person ?? "").trim(),
    fixed_quantity: String(form?.fixed_quantity ?? "").trim(),
    extra_price: String(form?.extra_price ?? "").trim(),
    is_default: String(form?.is_default ?? "N").trim(),
    display_order: String(form?.display_order ?? "").trim(),
    status: String(form?.status ?? "ACTIVE").trim(),
  });

  const saveOptionGroupItem = async (form, editing) => {
    if (optionGroupItemSaving) return false;
    const payload = buildOptionGroupItemPayload(form, editing);
    if (editing && !payload.option_group_item_id) {
      toast.error("Option Group Item ID is required");
      return false;
    }
    if (!payload.option_group_id)
      return (toast.error("Option Group is required"), false);
    if (!payload.package_id) return (toast.error("Package is required"), false);
    if (!payload.item_id) return (toast.error("Item is required"), false);
    try {
      setOptionGroupItemSaving(true);
      const res = await api.post(
        editing
          ? "/BulkCatalogue/optionGroupItemsUpdate"
          : "/BulkCatalogue/optionGroupItemsInsert",
        payload,
      );
      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Option group item save failed",
        );
        return false;
      }
      toast.success(
        res?.data?.responseMessage ||
          `Option group item ${editing ? "updated" : "added"} successfully`,
      );
      return true;
    } catch (error) {
      console.error(
        "optionGroupItems save error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error saving option group item"));
      return false;
    } finally {
      setOptionGroupItemSaving(false);
    }
  };

  const insertOptionGroupItem = (form) => saveOptionGroupItem(form, false);
  const updateOptionGroupItem = (form) => saveOptionGroupItem(form, true);

  const deleteOptionGroupItem = async (optionGroupItemId) => {
    const id = String(optionGroupItemId ?? "").trim();
    if (!id || optionGroupItemDeletingId) return false;
    try {
      setOptionGroupItemDeletingId(id);
      const res = await api.post("/BulkCatalogue/optionGroupItemsDelete", {
        option_group_item_id: id,
      });
      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Option group item delete failed",
        );
        return false;
      }
      toast.success(
        res?.data?.responseMessage || "Option group item deleted successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "optionGroupItemsDelete error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error deleting option group item"));
      return false;
    } finally {
      setOptionGroupItemDeletingId("");
    }
  };

  return {
    optionGroupsLoading,
    optionGroupSaving,
    optionGroupDeletingId,
    getOptionGroups,
    insertOptionGroup,
    updateOptionGroup,
    deleteOptionGroup,
    optionGroupItemsLoading,
    optionGroupItemSaving,
    optionGroupItemDeletingId,
    getOptionGroupItems,
    insertOptionGroupItem,
    updateOptionGroupItem,
    deleteOptionGroupItem,
  };
}
